import React, { useEffect, useMemo, useState } from "react";
import { Modal, Progress, Badge, Tabs, Tag, Button, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  fetchBenchmarkRun,
  fetchBenchmarkRunResults,
  BenchmarkResult,
} from "@/lib/api/resources/benchmark";
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  ArrowsAltOutlined,
} from "@ant-design/icons";
import { RunExecutionLog } from "./RunExecutionLog";
import { RunInsights } from "./RunInsights";
import { RunCasesTable } from "./RunCasesTable";

const { Text, Title } = Typography;

interface RunConsoleModalProps {
  runId: string;
  onClose: () => void;
}

const collectionToArray = (data: any): BenchmarkResult[] =>
  Array.isArray(data) ? data : data?.["hydra:member"] || data?.member || [];

export const RunConsoleModal: React.FC<RunConsoleModalProps> = ({
  runId,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<string>("log");
  const [switchedOnFinish, setSwitchedOnFinish] = useState(false);

  const { data: run, isLoading } = useQuery({
    queryKey: ["benchmarkRun", runId],
    queryFn: () => fetchBenchmarkRun(runId),
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = (query.state?.data as any)?.status;
      return status === "completed" || status === "failed" ? false : 1500;
    },
  });

  const { data: resultsResponse, isLoading: resultsLoading } = useQuery({
    queryKey: ["benchmarkRunResults", runId],
    queryFn: () => fetchBenchmarkRunResults(runId),
    enabled: !!runId,
    refetchInterval: () => {
      const status = run?.status;
      return status === "completed" || status === "failed" ? false : 1500;
    },
  });

  const results = useMemo(
    () => collectionToArray(resultsResponse),
    [resultsResponse],
  );
  const isFinished = run?.status === "completed" || run?.status === "failed";

  // When the run finishes, jump the user to the visual summary once (they can still go back to the log).
  useEffect(() => {
    if (isFinished && !switchedOnFinish) {
      setActiveTab("insights");
      setSwitchedOnFinish(true);
    }
  }, [isFinished, switchedOnFinish]);

  const progressPercent =
    run && run.totalCases > 0
      ? Math.floor((run.completedCases / run.totalCases) * 100)
      : 0;
  const successful = run ? run.completedCases - run.failedCases : 0;

  let statusBadge = {
    status: "processing",
    text: "Running",
    icon: <LoadingOutlined />,
  } as any;
  if (run?.status === "pending")
    statusBadge = {
      status: "warning",
      text: "Pending",
      icon: <LoadingOutlined />,
    };
  if (run?.status === "completed")
    statusBadge = {
      status: "success",
      text: "Completed",
      icon: <CheckCircleOutlined />,
    };
  if (run?.status === "failed")
    statusBadge = {
      status: "error",
      text: "Failed",
      icon: <CloseCircleOutlined />,
    };

  const runShortId = runId.split("/").pop()?.substring(0, 8);

  return (
    <Modal
      open
      onCancel={onClose}
      maskClosable={isFinished}
      footer={[
        run && (
          <Link key="details" to={`/benchmark/${runId.split("/").pop()}`}>
            <Button icon={<ArrowsAltOutlined />}>Open full page</Button>
          </Link>
        ),
        <Button
          key="close"
          type="primary"
          onClick={onClose}
          disabled={!isFinished && isLoading}
        >
          {isFinished ? "Close" : "Run in background"}
        </Button>,
      ]}
      centered
      width={1000}
      styles={{ body: { paddingTop: 8 } }}
      title={
        <div className="flex items-center gap-3 pr-6">
          <CodeOutlined className="text-blue-600" />
          <span className="font-bold">Benchmark Run Console</span>
          {run?.agent?.name && (
            <Tag color="blue" className="font-normal rounded">
              {run.agent.name}
            </Tag>
          )}
          {runShortId && (
            <span className="text-gray-400 font-mono text-xs">
              {runShortId}
            </span>
          )}
          <span className="ml-auto">
            <Badge
              status={statusBadge.status}
              text={
                <Text className="uppercase text-xs font-semibold tracking-wider">
                  {statusBadge.text}
                </Text>
              }
            />
          </span>
        </div>
      }
    >
      {!run && isLoading ? (
        <div className="p-12 text-center">
          <LoadingOutlined style={{ fontSize: 42, color: "#1677ff" }} />
          <Title level={4} className="!mt-4 !mb-1">
            Initializing Benchmark…
          </Title>
          <Text type="secondary">
            Waking up the agent and preparing the execution environment.
          </Text>
        </div>
      ) : run ? (
        <>
          {/* Persistent overall progress header — visible across all tabs */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 mb-4">
            <div className="flex justify-between items-end mb-2">
              <span className="font-semibold text-gray-700">
                Overall Progress
              </span>
              <span className="text-gray-500 font-mono text-sm">
                {run.completedCases} / {run.totalCases} cases
              </span>
            </div>
            <Progress
              percent={progressPercent}
              status={
                run.status === "failed"
                  ? "exception"
                  : run.status === "completed"
                    ? "success"
                    : "active"
              }
              size={12}
            />
            <div className="mt-3 grid grid-cols-4 gap-2 text-center divide-x divide-gray-200">
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wider">
                  Total
                </div>
                <div className="text-lg font-semibold font-mono text-gray-800">
                  {run.totalCases}
                </div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wider">
                  Successful
                </div>
                <div className="text-lg font-semibold font-mono text-emerald-600">
                  {successful}
                </div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wider">
                  Errored
                </div>
                <div
                  className={`text-lg font-semibold font-mono ${run.failedCases > 0 ? "text-red-500" : "text-gray-400"}`}
                >
                  {run.failedCases}
                </div>
              </div>
              <div>
                <div className="text-gray-400 text-[11px] uppercase tracking-wider">
                  Judge Avg
                </div>
                <div className="text-lg font-semibold font-mono text-purple-600">
                  {run.averageQualityScore != null
                    ? run.averageQualityScore.toFixed(1)
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          {run.status === "failed" && run.statusMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4 overflow-x-auto">
              <strong>Error:</strong> {(() => {
                try {
                  if (
                    run.statusMessage.trim().startsWith("{") ||
                    run.statusMessage.trim().startsWith("[")
                  ) {
                    return (
                      <pre className="mt-2 text-xs whitespace-pre-wrap">
                        {JSON.stringify(JSON.parse(run.statusMessage), null, 2)}
                      </pre>
                    );
                  }
                } catch {
                  // Fallback to raw string
                }
                return run.statusMessage;
              })()}
            </div>
          )}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "log",
                label: (
                  <span>
                    <CodeOutlined /> Live Execution{" "}
                    {!isFinished && <Badge dot status="processing" />}
                  </span>
                ),
                children: <RunExecutionLog run={run} results={results} />,
              },
              {
                key: "insights",
                label: (
                  <span>
                    <BarChartOutlined /> Summary & Insights
                  </span>
                ),
                children: <RunInsights run={run} results={results} />,
              },
              {
                key: "cases",
                label: (
                  <span>
                    <UnorderedListOutlined /> Test Cases ({results.length})
                  </span>
                ),
                children: (
                  <RunCasesTable
                    results={results}
                    loading={resultsLoading && results.length === 0}
                  />
                ),
              },
            ]}
          />
        </>
      ) : (
        <div className="p-12 text-center">
          <CloseCircleOutlined style={{ fontSize: 42, color: "#ff4d4f" }} />
          <Title level={4} className="!mt-4">
            Could not load this run
          </Title>
          <Button onClick={onClose} className="mt-4">
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default RunConsoleModal;
